import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { OFFICIAL_LOGO, FOUNDER_PORTRAIT } from "../lib/brandAssets";

const PILLARS = [
  {
    label: "Awaken",
    body:
      "Become conscious of what has shaped us, what we have inherited, and what has remained hidden within us.",
  },
  {
    label: "Re-Member",
    body:
      "Recover and reintegrate what fear, conditioning, fragmentation, and survival caused us to forget about ourselves and our relationship with the Divine.",
  },
  {
    label: "Return",
    body:
      "Embody what has been remembered through the way we live, love, create, relate, and participate in the world.",
  },
];

export default function About() {
  return (
    <div>
      <section className="relative overflow-hidden" style={{ background: "var(--surface-deep)" }}>
        <div className="grain absolute inset-0" />
        <div className="bloom absolute inset-0 opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative grid lg:grid-cols-12 gap-10 items-center" style={{ color: "var(--bg-cream)" }}>
          <div className="lg:col-span-7 rise">
            <div className="eyebrow mb-4" style={{ color: "var(--gold-soft)" }}>About the Temple</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5">
              <span style={{ color: "var(--bg-cream)" }}>Awaken.</span>{" "}
              <span style={{ color: "var(--rose)" }}>Re-Member.</span>{" "}
              <span style={{ color: "var(--gold)", fontStyle: "italic" }}>Return.</span>
            </h1>
            <p className="max-w-xl text-base sm:text-lg opacity-90 leading-relaxed">
              Temple of the Human Heart is a contemplative spiritual community centered on
              awakening, inner transformation, compassion, and returning to the Divine
              presence already within and among us. It is designed especially for truth
              seekers and people exploring spirituality beyond fear-based religious frameworks.
            </p>
            <blockquote className="mt-8 pl-4 border-l-2 font-serif italic text-lg max-w-lg" style={{ borderColor: "var(--gold)", color: "var(--gold-soft)" }}>
              "Love is the doctrine. Compassion is the practice. Awakening is the work."
            </blockquote>
          </div>
          <div className="lg:col-span-5 rise" style={{ animationDelay: "0.15s" }}>
            <div className="relative mx-auto max-w-[420px]">
              <div className="absolute -inset-3 rounded-3xl" style={{ background: "linear-gradient(135deg, var(--gold-soft), var(--rose))", opacity: 0.35 }} />
              <img
                src={OFFICIAL_LOGO}
                alt="Temple of the Human Heart — official brand logo"
                data-testid="about-official-logo"
                className="relative rounded-2xl w-full h-auto object-contain"
                style={{ background: "var(--obsidian)" }}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="mb-10 rise">
          <div className="eyebrow mb-2">Three movements</div>
          <h2 className="font-serif text-3xl sm:text-4xl" style={{ color: "var(--burgundy)" }}>
            The path of the Human Heart.
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <article
              key={p.label}
              data-testid={`about-pillar-${p.label.toLowerCase()}`}
              className="rounded-2xl border p-7 rise lift"
              style={{
                background: i === 1 ? "var(--burgundy)" : "var(--bg-cream-card)",
                color: i === 1 ? "var(--bg-cream)" : "var(--text-obsidian)",
                borderColor: i === 1 ? "var(--burgundy)" : "var(--border-cream)",
                animationDelay: `${i * 0.08}s`,
              }}
            >
              <div className="eyebrow mb-3" style={{ color: i === 1 ? "var(--gold-soft)" : "var(--gold-dark)" }}>
                Movement 0{i + 1}
              </div>
              <div className="font-serif text-3xl mb-3">{p.label}</div>
              <p className="text-sm leading-relaxed opacity-90">{p.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="border-y" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid md:grid-cols-5 gap-10 md:gap-12 items-center">
          <div className="md:col-span-2">
            <img
              src={FOUNDER_PORTRAIT}
              alt="Yisra'el Apriale — Love is the Doctrine."
              data-testid="about-founder-portrait"
              className="w-full max-w-md mx-auto md:max-w-none aspect-square rounded-3xl object-cover object-center"
              style={{ boxShadow: "0 24px 60px -30px rgba(48,43,41,0.45)" }}
            />
          </div>
          <div className="md:col-span-3">
            <div className="eyebrow mb-2">Founder</div>
            <h2 className="font-serif text-3xl leading-tight" style={{ color: "var(--burgundy)" }} data-testid="about-founder-name">
              Yisra'el Apriale
            </h2>
            <div className="text-sm mt-2 mb-6" style={{ color: "var(--text-muted)" }}>
              Founder &amp; Teacher
            </div>
            <div className="space-y-5 text-base leading-relaxed" style={{ color: "var(--text-obsidian)" }} data-testid="about-founder-copy">
              <p>
                Yisra'el Apriale is the founder and teacher of Temple of the Human Heart —
                a sanctuary shaped by decades of quiet listening and honest study of the
                interior life.
              </p>
              <p>
                Her work invites truth seekers into a spirituality that is warm rather than
                fearful, awake rather than performative, and rooted in the Divine presence
                already living within and among us.
              </p>
              <p className="italic" style={{ color: "var(--text-muted)" }}>
                The Temple is her offering to those who need somewhere gentle, contemplative,
                and true to arrive.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="rounded-3xl overflow-hidden relative" style={{ background: "var(--surface-deep)" }}>
          <div className="grain absolute inset-0" />
          <div className="relative p-8 sm:p-14 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="eyebrow mb-3" style={{ color: "var(--gold-soft)" }}>Come closer</div>
              <h3 className="font-serif text-3xl sm:text-4xl leading-tight" style={{ color: "var(--bg-cream)" }}>
                The door is already open.
              </h3>
              <p className="text-sm sm:text-base opacity-85 mt-3 max-w-lg" style={{ color: "var(--bg-cream)" }}>
                Listen to a teaching, read a reflection, or step into the Learning Circle whenever you are ready.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 lg:justify-end">
              <Link to="/join" data-testid="about-join-link"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium"
                style={{ background: "var(--gold)", color: "var(--burgundy)" }}>
                Become a member <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/listen" data-testid="about-listen-link"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium border"
                style={{ borderColor: "var(--gold-soft)", color: "var(--bg-cream)" }}>
                Listen first
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
